'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
/**
 * Hook for managing quiz state with localStorage persistence
 * @param {string} lang - Current language (ru/en)
 * @param {boolean} isClient - Whether we're on the client side
 * @returns {Object} Quiz state and management functions
 */
export function useQuizState(lang, isClient) {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const [error, setError] = useState('');

    const saveTimerRef = useRef(null);

    // Save quiz state to localStorage (debounced)
    const saveQuizState = useCallback((state) => {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            if (!isClient) return;
            try {
                localStorage.setItem('esimQuizPageState', JSON.stringify({
                    ...state,
                    language: lang
                }));
            } catch (error) {
                console.error('Error saving quiz state:', error);
            }
        }, 500);
    }, [isClient, lang]);

    useEffect(() => () => clearTimeout(saveTimerRef.current), []);

    // Load quiz state from localStorage
    const loadQuizState = useCallback(() => {
        if (!isClient) return null;
        try {
            const saved = localStorage.getItem('esimQuizPageState');
            if (saved) {
                const parsedState = JSON.parse(saved);
                // Only load if the language matches
                if (parsedState.language === lang) {
                    return parsedState;
                }
            }
            return null;
        } catch (error) {
            console.error('Error loading quiz state:', error);
            return null;
        }
    }, [isClient, lang]);

    // Clear quiz state from localStorage
    const clearQuizState = useCallback(() => {
        if (!isClient) return;
        try {
            localStorage.removeItem('esimQuizPageState');
        } catch (error) {
            console.error('Error clearing quiz state:', error);
        }
    }, [isClient]);

    // Load saved state on mount
    useEffect(() => {
        if (isClient) {
            const savedState = loadQuizState();
            if (savedState) {
                setCurrentQuestion(savedState.currentQuestion || 0);
                setAnswers(savedState.answers || {});
                setShowResults(savedState.showResults || false);
            }
        }
    }, [isClient, loadQuizState]);

    // Save state when it changes
    useEffect(() => {
        if (isClient && currentQuestion > 0) {
            saveQuizState({
                currentQuestion,
                answers,
                showResults
            });
        }
    }, [currentQuestion, answers, showResults, isClient, saveQuizState]);

    return {
        currentQuestion,
        setCurrentQuestion,
        answers,
        setAnswers,
        showResults,
        setShowResults,
        error,
        setError,
        saveQuizState,
        loadQuizState,
        clearQuizState
    };
}
